<template>
    <div :id="id" class="relative pb-20">
        <BlogHeaderBackground />

        <article v-if="article" class="markdown">
            <BlogArticleHeader :article="article" :reading-time="readingTime" />

            <div
                class="relative z-0 flex flex-col lg:grid lg:grid-cols-10 lg:gap-10"
            >
                <div class="flex flex-col lg:col-span-8">
                    <hr />

                    <!-- Content -->
                    <ContentRenderer
                        :id="articleContentId"
                        :value="article"
                        class="bg-white text-justify"
                    />
                </div>

                <!-- Table of Contents -->
                <BlogArticleToc
                    v-if="article.body?.toc?.links.length"
                    :article="article"
                    :active-id="activeId"
                />
            </div>

            <div class="mt-20 flex items-center justify-between">
                <NuxtLink
                    to="/blog"
                    class="text-base text-blue-400 no-underline hover:underline"
                >
                    {{ t('back-to-blog') }}
                </NuxtLink>

                <button
                    class="flex items-center gap-2 rounded-md px-2 py-1 text-base hover:bg-gray-100"
                    @click="copyArticleLink"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        class="size-6"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                        />
                    </svg>

                    {{ t('copy-link') }}
                </button>
            </div>

            <hr />

            <div class="not-prose grid grid-cols-1 gap-8 md:grid-cols-2">
                <BlogArticleNavigation
                    v-if="articlesNavigation.previous"
                    :article="articlesNavigation.previous"
                    direction="previous"
                />

                <BlogArticleNavigation
                    v-if="articlesNavigation.next"
                    :article="articlesNavigation.next"
                    direction="next"
                />
            </div>
        </article>
    </div>
</template>

<script setup lang="ts">
import type { BlogArticle } from '~/types/blog'

const route = useRoute()
const id = useId()
const { t } = useI18n()

const articleSlug = route.params.slug as string
const articleContentId = `blog-article-content-${id}`
const articlesNavigation = ref({
    previous: null as BlogArticle | null,
    next: null as BlogArticle | null,
})

const { EFIRMA_WEBSITE_URL } = useRuntimeConfig().public

const { data: article } = await useAsyncData(`/blog/${articleSlug}`, () =>
    queryContent<BlogArticle>(`/blog/${articleSlug}`).findOne(),
)

const { data: surround } = await useAsyncData(`${articleSlug}-surround`, () =>
    queryContent<BlogArticle>('/blog')
        .where({ _extension: 'md' })
        .sort({ date: 1 })
        .findSurround(`/blog/${articleSlug}`),
)

if (surround.value) {
    const [previous, next] = surround.value

    articlesNavigation.value = { previous, next }
}

if (!article.value) {
    throw createError({
        statusCode: 404,
        statusMessage: 'Article not found',
        fatal: true,
    })
}

useSeoMeta({
    ogUrl: `${EFIRMA_WEBSITE_URL}${article.value._path}`,
    title: article.value.title,
    ogTitle: article.value.title,
    description: article.value.description,
    ogDescription: article.value.description,
    ogType: 'article',
    creator: 'eFirma',
    ogImage: article.value.image || undefined,
    ogImageAlt: article.value.title,
})

const activeId = ref<string>('')
const readingTime = ref<number>()

let observer: IntersectionObserver

onMounted(() => {
    observeActiveSection()

    const textContent = document.querySelector(
        `#${articleContentId}`,
    )?.textContent

    if (textContent) {
        readingTime.value = calculateReadingTime(textContent)
    }
})

onUnmounted(() => {
    if (observer) observer.disconnect()
})

function observeActiveSection() {
    const headings = document.querySelectorAll(`#${id} h2`)

    activeId.value = headings[0]?.id || ''

    observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    activeId.value = entry.target.id
                }
            })
        },
        {
            rootMargin: '-10% 0px -60% 0px',
            threshold: 0,
        },
    )
    headings.forEach((heading) => observer.observe(heading))
}

function calculateReadingTime(text: string) {
    const wordsPerMinute = 240 // Average reading speed
    const words = text.split(/\s+/).length // Count total words
    const readingTime = Math.ceil(words / wordsPerMinute) // Round up to nearest minute

    return readingTime
}

async function copyArticleLink() {
    if (!article.value) return

    const url = `${EFIRMA_WEBSITE_URL}${article.value._path}`
    await navigator.clipboard.writeText(url)
}
</script>

<i18n lang="yaml">
en:
    back-to-blog: '<- Back to Blog'
    copy-link: 'Copy Link'
bg:
    back-to-blog: '<- Обратно към блога'
    copy-link: 'Копирай линка'
</i18n>
